// Shared shell for the 8 static routes (PROMPT 3.6).
import { Link } from "react-router-dom";
import { useCanonical, useSeoMeta } from "../lib/seo";

// Per-page search descriptions for support & policy pages.
const PAGE_DESCRIPTIONS = {
        "/about": "Loadout is an Indian gaming peripherals store run by Onyx Enterprises in Noida — handpicked keyboards, mice and mousepads with India warranty and support.",
        "/contact": "Contact Loadout — WhatsApp and phone +91 8368249423, support@loadout.co.in, Mon–Sat 10:00–19:00 IST. Business details and GSTIN for Onyx Enterprises, Noida.",
        "/faq": "Answers to common Loadout questions — shipping times across India, pre-orders, payments, UPI and EMI, warranty claims, returns and order tracking.",
        "/warranty": "Loadout's 18-month warranty on gaming keyboards and mice — what's covered, how to raise an RMA, the returns window and non-returnable items.",
        "/shipping-returns": "Loadout shipping and returns — 2–4 day dispatch for in-stock items, pre-order timelines, PIN-code coverage across India and how returns work.",
        "/preorder-policy": "How pre-orders work at Loadout — full pre-payment, arrival windows, cancellations, mixed carts with in-stock items and what happens if a batch is delayed.",
        "/privacy": "Loadout privacy policy — what personal data Onyx Enterprises collects, why, how long it is kept, and your rights under India's DPDP Act 2023.",
        "/terms": "Terms of Service for loadout.co.in — ordering, pricing and GST, payments via Razorpay, shipping, warranty, liability and the legal identity of Onyx Enterprises.",
        "/cookies": "Which cookies loadout.co.in uses — sign-in, cart and theme preferences — and how to control them in your browser.",
        "/track-order": "Track your Loadout order — the courier tracking link is sent to you by SMS, WhatsApp and email when your order dispatches, with live delivery status across India.",
};
import company from "../data/company.json";
import copy from "../lib/catalog/copy-strings.json";

const RELATED = {
        "/about": ["/contact", "/warranty", "/shipping-returns", "/"],
        "/contact": ["/faq", "/warranty", "/track-order", "/about"],
        "/shipping-returns": ["/warranty", "/track-order", "/preorder-policy", "/"],
        "/preorder-policy": ["/shipping-returns", "/warranty", "/track-order", "/faq"],
        "/faq": ["/contact", "/warranty"],
        "/track-order": ["/contact", "/warranty"],
        "/warranty": ["/contact", "/track-order", "/shipping-returns"],
        "/privacy": ["/contact"],
        "/terms": ["/contact"],
};

export function ConfirmBanner({ show, note = "Some slots on this page are awaiting operator input." }) {
        if (!show) return null;
        return (
                <div
                        data-testid="operator-confirm-banner"
                        className="border border-yellow-400/50 bg-yellow-400/10 text-yellow-200 px-4 py-3 my-6 font-mono text-[11px] tracking-[0.14em] uppercase"
                >
                        {note}
                </div>
        );
}

export function TrustStrip() {
        return (
                <div className="border-y border-white/10 py-4 mt-16">
                        <p className="text-center font-mono text-[10px] md:text-[11px] tracking-[0.16em] uppercase text-white/60">
                                {copy.trust_strip}
                        </p>
                </div>
        );
}

export function RelatedPages({ path }) {
        const targets = RELATED[path] || [];
        if (!targets.length) return null;
        return (
                <nav
                        data-testid="related-pages"
                        className="mt-10 border-t border-white/10 pt-6"
                        aria-label="Related pages"
                >
                        <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-white/50">
                                Related pages
                        </p>
                        <div className="mt-3 flex flex-wrap gap-4">
                                {targets.map((t) => (
                                        <Link
                                                key={t}
                                                to={t}
                                                data-testid={`related-link-${t.replace(/\//g, "") || "home"}`}
                                                className="min-h-[48px] inline-flex items-center border border-white/20 hover:border-[#F59E0B] hover:text-[#F59E0B] text-white font-mono text-[12px] tracking-[0.14em] uppercase px-4 py-2 transition-colors"
                                        >
                                                {copy.related_labels[t] || t}
                                        </Link>
                                ))}
                        </div>
                </nav>
        );
}

export default function PageShell({ title, path, description, children, hasConfirm = false }) {
        useSeoMeta({ title, path, description: description || PAGE_DESCRIPTIONS[path] });
        useCanonical(path);
        // Robots + CSP meta on mount.
        if (typeof document !== "undefined") {
                let robots = document.querySelector('meta[name="robots"]');
                if (!robots) {
                        robots = document.createElement("meta");
                        robots.setAttribute("name", "robots");
                        document.head.appendChild(robots);
                }
                robots.setAttribute("content", "index,follow");

                // PROMPT 3.6 R9 / T50g — CSP for /track-order Discord widget frame-src.
                if (path === "/track-order") {
                        let csp = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
                        if (!csp) {
                                csp = document.createElement("meta");
                                csp.setAttribute("http-equiv", "Content-Security-Policy");
                                document.head.appendChild(csp);
                        }
                        csp.setAttribute("content",
                                "frame-src https://discord.com https://discordapp.com https://ptb.discord.com https://canary.discord.com; " +
                                "img-src 'self' https: data: blob: https://cdn.discordapp.com;"
                        );
                }
        }

        return (
                <div className="min-h-screen bg-black text-white" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
                        <header className="sticky top-0 z-40 bg-black border-b border-white/10">
                                <div className="mx-auto max-w-[1440px] px-6 py-4 flex items-center justify-between">
                                        <Link to="/" className="flex items-center gap-2">
                                                <span className="font-display text-lg tracking-widest uppercase">Loadout</span>
                                        </Link>
                                        <nav className="hidden lg:flex gap-6 font-mono text-[11px] tracking-[0.16em] uppercase text-white/70">
                                                <Link to="/about" className="hover:text-[#F59E0B]">About</Link>
                                                <Link to="/contact" className="hover:text-[#F59E0B]">Contact</Link>
                                                <Link to="/track-order" className="hover:text-[#F59E0B]">Track Order</Link>
                                                <Link to="/warranty" className="hover:text-[#F59E0B]">Warranty</Link>
                                        </nav>
                                        <button
                                                type="button"
                                                data-testid="mobile-menu-hamburger"
                                                aria-label="Menu"
                                                className="lg:hidden min-h-[48px] min-w-[48px] flex items-center justify-center"
                                                onClick={() => { window.location.href = "/#menu"; }}
                                        >
                                                <span className="sr-only">Menu</span>
                                                <div className="flex flex-col gap-1.5">
                                                        <span className="block w-6 h-0.5 bg-white" />
                                                        <span className="block w-6 h-0.5 bg-white" />
                                                        <span className="block w-6 h-0.5 bg-white" />
                                                </div>
                                        </button>
                                </div>
                        </header>

                        <main className="mx-auto max-w-[1024px] px-6 py-14 md:py-20">
                                <ConfirmBanner show={hasConfirm} />
                                {children}
                                <RelatedPages path={path} />
                        </main>

                        <TrustStrip />

                        <footer className="bg-black border-t border-white/10 mt-6">
                                <div className="mx-auto max-w-[1440px] px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
                                        <div>
                                                <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/40">Brand</p>
                                                <Link to="/about" className="mt-3 block text-white/80 hover:text-[#F59E0B] text-sm">
                                                        About / Loadout by Onyx Enterprises
                                                </Link>
                                        </div>
                                        <div>
                                                <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/40">Support</p>
                                                <ul className="mt-3 space-y-2 text-sm">
                                                        <li><Link to="/contact" className="text-white/80 hover:text-[#F59E0B]">Contact</Link></li>
                                                        <li><Link to="/shipping-returns" className="text-white/80 hover:text-[#F59E0B]">Shipping &amp; Returns</Link></li>
                                                        <li><Link to="/shipping-returns#refunds" data-testid="pageshell-footer-refund-link" className="text-white/80 hover:text-[#F59E0B]">Refund Policy</Link></li>
                                                        <li><Link to="/preorder-policy" className="text-white/80 hover:text-[#F59E0B]">Pre-Order Policy</Link></li>
                                                        <li><Link to="/faq" className="text-white/80 hover:text-[#F59E0B]">FAQ</Link></li>
                                                        <li><Link to="/track-order" className="text-white/80 hover:text-[#F59E0B]">Track Order</Link></li>
                                                        <li><Link to="/warranty" className="text-white/80 hover:text-[#F59E0B]">Warranty &amp; RMA</Link></li>
                                                        <li><Link to="/privacy" className="text-white/80 hover:text-[#F59E0B]">Privacy</Link></li>
                                                        <li><Link to="/terms" className="text-white/80 hover:text-[#F59E0B]">Terms</Link></li>
                                                </ul>
                                        </div>
                                        <div className="col-span-2 md:col-span-2 text-sm text-white/60">
                                                <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/40 mb-3">Reach us</p>
                                                <p>{company.address_city}, {company.address_country}</p>
                                                <p className="mt-1">{company.operating_hours}</p>
                                                <p className="mt-1">
                                                        <a href={`https://wa.me/${company.whatsapp_phone_e164}`} className="hover:text-[#F59E0B]">
                                                                WhatsApp {company.business_phone}
                                                        </a>
                                                </p>
                                                <p className="mt-1">
                                                        <a href={`mailto:${company.support_email}`} className="hover:text-[#F59E0B]">
                                                                {company.support_email}
                                                        </a>
                                                </p>
                                                <p className="mt-3 font-mono text-[12px] text-white/60" data-testid="pageshell-footer-gstin">
                                                        {company.legal_name} · GSTIN {company.gstin}
                                                </p>
                                                <Link to="/contact#business-legal" data-testid="pageshell-footer-business-legal-link" className="mt-1 inline-block text-[#F59E0B] hover:underline">
                                                        Business &amp; legal info →
                                                </Link>
                                                <p className="mt-3 flex gap-4">
                                                        <a href="https://www.instagram.com/loadout.co.in/" target="_blank" rel="noreferrer noopener" data-testid="pageshell-footer-instagram" className="hover:text-[#F59E0B]">Instagram</a>
                                                        <a href="https://discord.gg/ya788cmkVS" target="_blank" rel="noreferrer noopener" data-testid="pageshell-footer-discord" className="hover:text-[#F59E0B]">Discord</a>
                                                </p>
                                        </div>
                                </div>
                                <div className="border-t border-white/10 px-6 py-4 text-center font-mono text-[10px] tracking-[0.16em] uppercase text-white/40">
                                        © {new Date().getFullYear()} Onyx Enterprises — Loadout is a brand of Onyx Enterprises.
                                </div>
                        </footer>
                </div>
        );
}

// Utility: does any string contain [CONFIRM: (…)]?
export function hasConfirmSlot(...vals) {
        return vals.some((v) => typeof v === "string" && v.includes("[CONFIRM:"));
}
